<template>
  <div class="admission-documents-container">
    <c-loader v-if="isLoading" :size="isMobile ? 79 : 99" />

    <admission-document-step
      ref="step"
      v-show="!isLoading"
      :is-female="isFemale"
      :is-foreigner="isForeigner"
      :documents="documents"
      :custom-documents="customDocuments"
      :options="options"
      @submit="onSubmit"
      @edit="onEdit"
      @load-cities="loadCities"
    />
  </div>
</template>

<script>
import { Loadable, MediaQuery } from '@convenia/mixins'
import { AdmissionDocumentStep } from '@convenia/documents-organisms'
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import { SET_ACTIONS, GET_ACTIONS, BRAZILLIAN_IDS } from '@content/consts/Documents'
import * as types from '@types'

export default {
  name: 'DocumentsStepContainer',

  mixins: [ MediaQuery, Loadable() ],

  components: {
    AdmissionDocumentStep,
  },

  computed: {
    ...mapState({
      customDocuments: state => state.document.customDocuments,
      states: state => state.location.states,
      personalInfo: state => state.personalInfo.personal,
      employeeId: state => state.admission.employeeUuid,
    }),

    ...mapGetters({
      documents: types.DOCUMENT_GET_DOCUMENTS,
    }),

    isFemale () {
      const { gender, genderIdentities } = this.personalInfo || {}

      return gender === 2 || genderIdentities === 2
    },

    isForeigner () {
      const { nationalities = [] } = this.personalInfo || {}
      const isNotForeigner = !nationalities?.length
        || nationalities.some(id => BRAZILLIAN_IDS.includes(id))

      return !isNotForeigner
    },

    isCustomDocument () {
      return Object.keys(this.customDocuments)
        .findIndex(id => id === this.active) >= 0
    },

    selected () {
      if (!this.documents[this.active]) return {}

      return this.documents[this.active]
    },

    options () {
      return {
        states: this.states,
        issuing_state: this.states,
        cities: this.cities,
      }
    },
  },

  data: () => ({
    showModal: true,
    cities: [],
    active: null,
  }),

  mounted () {
    this.$root.$on('next', this.onNext)
    this.$root.$on('previous', this.onSave)
  },

  beforeDestroy () {
    this.$root.$off('next', this.onNext)
    this.$root.$off('previous', this.onSave)

    this.resetDocuments()
  },

  methods: {
    ...mapMutations({
      setFiles: types.DOCUMENT_SET_FILES,
      resetDocuments: types.DOCUMENT_RESET,
    }),

    ...mapActions({
      deleteFile: types.ADMISSION_DELETE_FILE,
      uploadFiles: types.DOCUMENT_UPLOAD_FILES,
      getDocuments: types.DOCUMENT_GET_DOCUMENTS,
      getCustomDocuments: types.DOCUMENT_CUSTOM_DOCUMENTS,
      getCustomFields: types.DOCUMENT_CUSTOM_FIELDS,
      getStates: types.LOCATION_STATES,
      getCities: types.LOCATION_CITIES,
      validateDocuments: types.DOCUMENT_VALIDATE_DOCUMENTS,
    }),

    async load () {
      await this.getDocuments()
      await this.getCustomDocuments()
      await this.getCustomFields()
      await this.getStates()

      this.isLoading = false

      await this.verify()

      this.$emit('load')
    },

    async onNext () {
      const { validated, errors } = this.$refs.step?.validate() || {}
      const valid = await this.validateDocuments({ validated, errors })

      if (!valid) return

      this.$emit('next')
    },

    onSave () {
      this.$emit('previous')
    },

    async onEdit (key) {
      this.active = key
    },

    async validate () {
      if (this.isLoading) return false

      const { validated, errors } = this.$refs.step?.validate() || {}

      const valid = await this.validateDocuments({ validated, errors })

      return valid
    },

    async verify () {
      if (this.isLoading) return false

      const { validated, errors } = this.$refs.step?.verify() || {}

      const valid = await this.validateDocuments({ validated, errors })

      this.$emit('verify', valid)

      return valid
    },

    submit: async () => {},

    async onSubmit (event) {
      this.showRemoveFileModal = false

      const { id, key, data, addedFiles, replacedFiles, callback } = event
      const type = this.isCustomDocument ? 'custom_field_value' : key
      const getAction = GET_ACTIONS[type]
      const updateAction = SET_ACTIONS[type]

      const [ err, document ] = await this.$store.dispatch(updateAction, { id, ...data })

      if (err)
        return callback(err)

      await this.deleteFiles(replacedFiles)

      const [ uploadErr ] = await this.uploadFiles({
        id: this.selected.customFieldValueId || document.id,
        type,
        files: addedFiles,
      })

      if (uploadErr)
        return callback(uploadErr)

      await this.$store.dispatch(getAction)
      await this.getCustomFields()
      await this.verify()

      callback()

      this.clear()
    },

    async deleteFiles (files) {
      const { employeeId } = this

      await Promise.all(files.map(
        file => this.deleteFile({ file, employeeId }))
      )
    },

    async loadCities (stateId) {
      if (!stateId) return

      this.cities = await this.getCities(stateId)
    },

    clear () {
      this.active = null
      this.cities = []
    }
  }
}
</script>

<style lang="scss">
.admission-documents-container {
  position: relative;

  & > .c-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%)
  }

  & > .admission-documents-step {
    margin-top: 20px;
  }
}
</style>
